// One PDF per label type: the master file in Drive. COHESION_CHANGE_LOG.md list 2 item 2 (approved 2026-10-25),
// mockup MOCKUP_ONE_PRINT_REV5.html (7:38 AM, "approved").
//
// The master holds every label for a roll, sorted by room, with a room page first in each room. On the
// roll the room page prints as one label and marks where the room starts. Print only certain pages and
// it's skipped.
//
// No label's drawing changes (Robert's rule, 2026-10-24). Each room's labels are drawn by the same code that
// always drew them, then copied page for page into the master. The room page is the only new drawing.
//
// Pure page numbering lives here too, so the card and the tests agree on which page holds which label.

import { degrees, PDFDocument, rgb, StandardFonts } from "pdf-lib";

export interface MasterSection {
  /** "ER1". Blank for a roll with no rooms (QEW tags): no room page. */
  room: string;
  /** Under the room on its page: "8 labels", "8 labels · WARNING". */
  line: string;
  /** The room's labels, drawn by the label's own code, one label per page. */
  bytes: Uint8Array;
}

/** One room page at the roll's size: the room big, the line under it. Searchable text. */
function drawRoomPage(pdf: PDFDocument, w: number, h: number, turn: number, room: string, line: string, bold: Awaited<ReturnType<PDFDocument["embedFont"]>>, plain: Awaited<ReturnType<PDFDocument["embedFont"]>>): void {
  const page = pdf.addPage([w, h]);
  // Turned the way the labels are (arc flash prints turned 180°), so it comes off the roll the same way up.
  if (turn) page.setRotation(degrees(turn));
  const inset = Math.min(w, h) * 0.06;
  page.drawRectangle({ x: inset, y: inset, width: w - inset * 2, height: h - inset * 2, borderColor: rgb(0, 0, 0), borderWidth: 1, borderDashArray: [3, 2] });
  const fit = (t: string, start: number, font: typeof bold, max: number) => {
    let s = start;
    while (s > 4 && font.widthOfTextAtSize(t, s) > max) s -= 0.5;
    return s;
  };
  const big = fit(room, h * 0.42, bold, w - inset * 4);
  const small = fit(line, Math.max(5, h * 0.13), plain, w - inset * 4);
  const total = big * 0.72 + small * 1.4;
  const top = (h + total) / 2;
  page.drawText(room, { x: (w - bold.widthOfTextAtSize(room, big)) / 2, y: top - big * 0.72, size: big, font: bold, color: rgb(0, 0, 0) });
  page.drawText(line, { x: (w - plain.widthOfTextAtSize(line, small)) / 2, y: top - big * 0.72 - small * 1.4, size: small, font: plain, color: rgb(0, 0, 0) });
}

/** Puts the rooms together: room page, then that room's labels, room by room. Pages copied untouched. */
export async function assembleMaster(title: string, sections: readonly MasterSection[]): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setTitle(title);
  pdf.setCreator("SafeTag Print");
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const plain = await pdf.embedFont(StandardFonts.Helvetica);
  for (const s of sections) {
    const src = await PDFDocument.load(s.bytes);
    const pages = await pdf.copyPages(src, src.getPageIndices());
    if (!pages.length) continue;
    if (s.room) {
      const { width, height } = pages[0]!.getSize();
      drawRoomPage(pdf, width, height, pages[0]!.getRotation().angle, s.room, s.line, bold, plain);
    }
    for (const p of pages) pdf.addPage(p);
  }
  return pdf.save();
}

// ---- Page numbers ----

export interface PagedItem<T> {
  n: number;
  /** A room page: the room and how many labels follow it. */
  room?: { name: string; count: number };
  item?: T;
}

/** The master's pages in order. Items come in print order; a room page goes before each room's first. */
export function masterPages<T extends { room: string }>(items: readonly T[], withRooms: boolean): PagedItem<T>[] {
  const out: PagedItem<T>[] = [];
  let n = 1;
  const order: string[] = [];
  for (const i of items) if (!order.includes(i.room)) order.push(i.room);
  for (const r of order) {
    const mine = items.filter((i) => i.room === r);
    if (withRooms && r) out.push({ n: n++, room: { name: r, count: mine.length } });
    for (const i of mine) out.push({ n: n++, item: i });
  }
  return out;
}

const runs = (nums: readonly number[]): [number, number][] => {
  const s = [...new Set(nums)].filter((n) => n > 0).sort((a, b) => a - b);
  const out: [number, number][] = [];
  for (const n of s) {
    const last = out[out.length - 1];
    if (last && n === last[1] + 1) last[1] = n;
    else out.push([n, n]);
  }
  return out;
};

/** "4, 9 to 10, 17": for the card. */
export function pagesWords(nums: readonly number[]): string {
  return runs(nums)
    .map(([a, b]) => (a === b ? `${a}` : `${a} to ${b}`))
    .join(", ");
}

/** "4,9-10,17": what a print dialog's Pages box takes. */
export function pagesForDialog(nums: readonly number[]): string {
  return runs(nums)
    .map(([a, b]) => (a === b ? `${a}` : `${a}-${b}`))
    .join(",");
}
